/*
 * Copyright (c) 2024 raqib. All rights reserved.
 * Author: Hamza Essalhi
 * Description: raqib Medical Dashboard - a comprehensive platform tailored for healthcare professionals,
 *  facilitating seamless patient management and enhancing practitioner satisfaction. With its intuitive interface
 *  and robust features, raqib empowers doctors to efficiently organize patient records, track medical histories,
 *  and streamline communication within their practice. By providing a user-friendly experience and customizable tools,
 *  raqib simplifies administrative tasks, allowing doctors to focus more on patient care and less on paperwork.
 */
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from "react-router-dom";
import React, { useEffect, Suspense } from "react";
import "./styles/App.css";
// Public Route Components (lazy)

const Login = React.lazy(() => import("./public/Login"));
const NotFound = React.lazy(() => import("./public/NotFound"));
const GeoErrorPage = React.lazy(() => import("./public/GeoErrorPage"));
const ErrorPage = React.lazy(() => import("./public/ErrorPage"));

// Private Route Components (lazy)

const Profile = React.lazy(() => import("./private/profile/Profile"));
const Patients = React.lazy(() => import("./private/patients/Patients"));
const AddPatient = React.lazy(() => import("./private/patients/AddPatient"));
const Settings = React.lazy(() => import("./private/Settings"));
const Medicaments = React.lazy(
  () => import("./private/medicaments/Medicaments"),
);
const Payments = React.lazy(() => import("./private/payments/Payments"));
const Appointments = React.lazy(
  () => import("./private/appointments/Appointments"),
);
const Team = React.lazy(() => import("./private/team/Team"));
const TeamMemberProfile = React.lazy(
  () => import("./private/team/TeamMemberProfile"),
);

const Visits = React.lazy(() => import("./private/visits/Visits"));
const MyVisits = React.lazy(() => import("./private/visits/MyVisits"));
const ViewVisit = React.lazy(() => import("./private/visits/ViewVisit"));

const VisitMedical = React.lazy(() => import("./private/visits/VisitMedical"));
const Reception = React.lazy(() => import("./private/reception/Reception"));
const Triage = React.lazy(() => import("./private/triage/Triage"));
const TriageByVisitId = React.lazy(
  () => import("./private/triage/TriageByVisitId"),
);
const MyTriageRecords = React.lazy(
  () => import("./private/triage/MyTriageRecords"),
);
const ClinicTriages = React.lazy(
  () => import("./private/triage/ClinicTriages"),
);
const MyRequests = React.lazy(() => import("./private/request/MyRequests"));
const RequestDetail = React.lazy(
  () => import("./private/request/RequestDetail"),
);

import AuthRouteController from "./controllers/AuthRouteController";
import PrivateRouteController from "./controllers/PrivateRouteController";

import { useUserRoles } from "./authentication/authentication";

import { useSelector } from "react-redux";
import { GlobalDebug } from "./utils/remove-consoles";
import { AlertContainer } from "./components/AlertContainer";

import LoadingComponent from "./components/common/LoadingComponent";
import CreateAppointment from "./private/appointments/CreateAppointment";
import Appointment from "./private/appointments/Appointment";
import CreatePayment from "./private/payments/CreatePayment";
import Payment from "./private/payments/Payment";
import VisitPayment from "./private/payments/VisitPayment";
import RaqibDashboard from "./private/Dashboard/RaqibDashboard";
import Statistics from "./private/statistics/Statistics";
import WaitingRoom from "./private/WaitingRoom/WaitingRoom";
import Subscription from "./private/Subscription";
import { isAdminRole } from "./utils/adminChecker";
import CreateRequest from "./private/request/CreateRequest";
import ClinicMaterials from "./private/materials/ClinicMaterials";
import PublicRouteController from "./controllers/PublicRouteController";
import Home from "./public/Home";
import PatientProfile from "./private/patients/PatientProfile";


const ToolsAndEquipment = React.lazy(
  () => import("./private/toolsAndEquipment/ToolsAndEquipment"),
);

// Main App Component
function App() {
  const unauthorizedError =
    useSelector((state) => state?.api?.unauthorizedError) || false;
  const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage,
  );
  const darkMode = useSelector((state) => state.settings.darkMode);

  const roles = useUserRoles();

  // add error state selector
  const errorState = useSelector((state) => state?.error) || null;

  let isAdmin = false;
  if (isAuthenticated) {
    isAdmin = isAdminRole(roles);
  }

  if (process.env.NODE_ENV !== "development") {
    GlobalDebug(false);
  }

  const router = createBrowserRouter(
    createRoutesFromElements(
      <React.Fragment>
        {/* Public Routes */}
        <Route path="/" element={<PublicRouteController />}>
          <Route path="/" element={<Home />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        {/* Authentication Routes */}
        <Route path="/" element={<AuthRouteController />}>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Private Routes */}
        {isAuthenticated && (
          <Route path="/" element={<PrivateRouteController />}>
            <Route path="/profile" element={<Profile />} />
            <Route path="/patients" element={<Patients />} />
            <Route path="/create-patient" element={<AddPatient />} />
            <Route path="/patients/:id" element={<PatientProfile />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/medicaments" element={<Medicaments />} />
            
            <Route path="/clinic-visits" element={<Visits />} />
            <Route path="/my-visits" element={<MyVisits />} />
            <Route path="/visits/:visitId" element={<ViewVisit />} />
    
            <Route path="/billings" element={<Payments />} />
            <Route path="/create-billing" element={<CreatePayment />} />
            <Route path="/billings/:id" element={<Payment />} />
            <Route path="/billing/:id" element={<VisitPayment />} />

            <Route path="/dashboard" element={<RaqibDashboard />} />

            <Route path="/appointments" element={<Appointments />} />
            <Route path="/create-appointment" element={<CreateAppointment />} />
            <Route path="/appointments/:id" element={<Appointment />} />

            {isAdmin && <Route path="/statistics" element={<Statistics />} />}

            <Route path="/waiting-room" element={<WaitingRoom />} />
            <Route path="/reception" element={<Reception />} />
            <Route path="/visit-medical/:visitId" element={<VisitMedical />} />
            <Route path="/triage/:visitId" element={<TriageByVisitId />} />
            <Route path="/my-triages" element={<MyTriageRecords />} />
            <Route path="/clinic-triages" element={<ClinicTriages />} />
            <Route path="/triage" element={<Triage />} />
            
            <Route path="/materials" element={<ClinicMaterials />} />
            <Route path="/tools-equipment" element={<ToolsAndEquipment />} />
            {/* Socket Test Route */}

            {isAdmin && (
              <>
                <Route path="/team" element={<Team />} />
                <Route path="/team/:id" element={<TeamMemberProfile />} />
                <Route
              path="/create-request/:requestType"
              element={<CreateRequest />}
            />
            <Route path="/create-request/" element={<CreateRequest />} />
            <Route path="/my-requests" element={<MyRequests />} />
            <Route
              path="/request/:id/:requestType"
              element={<RequestDetail />}
            />
              </>
            )}
            <Route path="*" element={<NotFound />} />
          </Route>
        )}
        {errorState?.errType && (
          <Route
            path="/error"
            element={<ErrorPage error={errorState.message} />}
          />
        )}
        {isAuthenticated && (
          <Route path="/subscription" element={<Subscription />} />
        )}
      </React.Fragment>,
    ),
    {
      navigationScroll: "manual",
      scrollRestoration: "manual",
      detectScroll: (prevLocation, location, history) => {
        if (prevLocation?.pathname !== location.pathname) {
          window.scrollTo(0, 0);
        }
      },
    },
  );

  useEffect(() => {
    if (defaultLanguage === "ar") {
      const style = document.createElement("style");
      style.innerHTML = `
      .App { direction: rtl !important; text-align: right !important; }
    `;
      document.head.appendChild(style);

      return () => {
        document.head.removeChild(style);
      };
    }
  }, [defaultLanguage]);




  return (
    <div
      className={`App ${darkMode ? "dark-scrollbar" : "light-scrollbar"}`}
      style={
        defaultLanguage === "ar"
          ? {
              direction: "rtl",
              textAlign: "right",
            }
          : {
              direction: "ltr",
              textAlign: "left",
            }
      }
    >
      <AlertContainer alertDuration={3000} />
      <Suspense fallback={<LoadingComponent />}>
{!unauthorizedError && <RouterProvider router={router} />} 
      </Suspense>
    </div>
  );
}

// Exporting the App component
export default App;
